import {styled} from '@linaria/react';
import React, {useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Text} from '../Text/Text';

type ActiveStyle = {
  backgroundColor: string;
  backgroundColorFocused?: string;
  borderColor: string;
};

interface TextAreaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  activeStyle: ActiveStyle;
  placeholder: string;
  height?: number;
  showCharacterCount?: boolean;
  maxLength?: number;
  errorMessage?: string;
}

export const TextArea = ({
  className,
  activeStyle,
  placeholder,
  height = 180,
  showCharacterCount = true,
  maxLength,
  errorMessage,
  onChange,
  ref,
  ...rest
}: TextAreaProps & {ref?: React.Ref<HTMLTextAreaElement>}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string>();

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      data-is-focused={isFocused}
      data-has-value={Boolean(value)}
      data-has-error={Boolean(errorMessage)}
    >
      <Placeholder weight="mediumBold" {...activeStyle}>
        {placeholder}
      </Placeholder>
      <StyledTextArea
        ref={ref}
        $height={height}
        maxLength={maxLength}
        {...rest}
        {...activeStyle}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      <Footer>
        {errorMessage && (
          <Error size="px12" weight="mediumBold" color="NotificationsRed100">
            {errorMessage}
          </Error>
        )}
        {showCharacterCount && (
          <CharsCount as="p" color="Grey1" size="px12" weight="mediumBold">
            {value?.length ?? 0} / {maxLength}
          </CharsCount>
        )}
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Placeholder = styled(Text)<ActiveStyle>`
  position: absolute;
  top: 16px;
  left: 16px;
  color: ${COLORS.Grey1};

  transition: transform 0.1s, color 0.1s;

  [data-is-focused='true'] &,
  [data-has-value='true'] & {
    color: ${COLORS.Black};
    background: ${props =>
      props.backgroundColorFocused ?? props.backgroundColor};
    font-size: ${FONT_SIZES.px10};
    padding-inline: 4px;
    transform: translateY(-21px);
  }

  [data-has-error='true'] & {
    color: ${COLORS.NotificationsRed100};
  }
`;

const StyledTextArea = styled.textarea<ActiveStyle & {$height: number}>`
  width: 100%;
  height: ${props => props.$height}px;
  padding: 14px 16px;
  border-radius: 4px;
  resize: none;
  background: ${COLORS.Grey4};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;
  border: 1px solid ${COLORS.Grey3};

  ${tabletAndUpLinaria(`padding: 16px;`)};

  [data-is-focused='true'] &,
  [data-has-value='true'] & {
    background: ${props =>
      props.backgroundColorFocused ?? props.backgroundColor};
  }

  [data-is-focused='true'][data-has-error='false'] & {
    border: ${props => `1px solid ${props.borderColor}`};
  }

  [data-has-error='false'] & {
    &:hover {
      border: ${props => `1px solid ${props.borderColor}`};
    }
  }

  [data-has-error='true'] & {
    border: 1px solid ${COLORS.NotificationsRed100};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
`;

const CharsCount = styled(Text)`
  width: 100%;
  text-align: right;
  margin: 4px 0 0;
`;

const Error = styled(Text)`
  white-space: nowrap;
`;
